import { memo } from 'react';

import { FileStatusIconProgressing, FileStatusSubtitle, FileStatusTitle } from '@air/feature-file-status/ui';

import { type CaptionVideoTaskMetadata } from '~/components/CaptionVideoTasks/types';
import { getTaskPaneFileStatusIconSize } from '~/components/FileStatusTrackingPane/getTaskPaneFileStatusIconSize';
import { PaneItemTW } from '~/components/FileStatusTrackingPane/PaneItemTW';
import { TaskPaneDismissButton } from '~/components/FileStatusTrackingPane/TaskPaneDismissButton';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type CaptionVideoTaskPanelItemInProgressProps = {
  metadata: CaptionVideoTaskMetadata;
  onCancel: () => void;
};

const getCaptionInProgressTitle = ({ isAssetVersion, isUi2026 }: { isAssetVersion: boolean; isUi2026: boolean }) => {
  if (isUi2026) {
    return isAssetVersion ? 'Saving caption version' : 'Saving caption asset';
  }

  return isAssetVersion ? 'Saving captions as new version' : 'Saving captions as new asset';
};

export const CaptionVideoTaskPanelItemInProgress = memo(
  ({ metadata, onCancel }: CaptionVideoTaskPanelItemInProgressProps) => {
    const { ui2026 } = useTemporaryFeatureFlags();
    const isAssetVersion = metadata.saveResultAs?.as === 'assetVersion';
    const inProgressTitle = getCaptionInProgressTitle({ isAssetVersion, isUi2026: ui2026 });

    return (
      <PaneItemTW
        avatar={<FileStatusIconProgressing size={getTaskPaneFileStatusIconSize(ui2026)} />}
        title={
          <FileStatusTitle data-testid="CAPTION_VIDEO_PANEL_IN_PROGRESS_TITLE">
            {ui2026 ? `${inProgressTitle}…` : inProgressTitle}
          </FileStatusTitle>
        }
        subtitle={
          ui2026 ? undefined : <FileStatusSubtitle className="truncate">Processing video...</FileStatusSubtitle>
        }
        buttons={<TaskPaneDismissButton label="Cancel" onClick={onCancel} />}
      />
    );
  },
);

CaptionVideoTaskPanelItemInProgress.displayName = 'CaptionVideoTaskPanelItemInProgress';
